/**
 * Gobodo User Provider
 * 
 * Implements user management using Gobodo UsersApi for persistence.
 * Custom properties are stored in the UserSchema `data` property.
 */

import * as GobodoApi from '@gobodo/gobodo-api-promise';
import { getGobodoClient } from '../../gobodo/gobodoClient';
import GobodoConfig, { getCustomSuffix, getSchemaId } from '../../config/gobodoConfig';
import {
  User,
  CreateUserPayload,
  UserFilters,
} from '../queries/userQueries';
import { serializeGobodoData } from './gobodoRoleProvider';
import type { Role } from './roleProvider';
import { convertFromGobodoRole } from './gobodoRoleProvider';
import { handleGobodoError, logGobodoError } from '../../utils/gobodoErrorHandler';
import { retryWithBackoff } from '../../utils/gobodoRetry';

/**
 * Convert Gobodo UserSchema to our User interface
 */
function convertFromGobodoUser(gobodoUser: any): User {
  try {
    // Serialize the entire Gobodo response to convert wrapped Gobodo values
    const serialized = serializeGobodoData(gobodoUser);
    
    // Graph traversal returns object rows with properties; UsersApi returns built-in fields + data.
    const data = serialized.data || {};
    const properties = serialized.properties || {};
    const flat = { ...serialized, ...properties, ...data };
    
    // Helper to safely convert dates
    const safeDateToString = (date: any): string => {
      if (!date) return new Date().toISOString();
      if (typeof date === 'string') {
        // Try to parse and convert to ISO string
        const parsed = new Date(date);
        return isNaN(parsed.getTime()) ? new Date().toISOString() : parsed.toISOString();
      }
      if (date instanceof Date) {
        return date.toISOString();
      }
      if (date.toISOString) {
        return date.toISOString();
      }
      return new Date().toISOString();
    };
    
    const rawKey =
      serialized.key ??
      serialized.objectkey ??
      serialized.objectKey ??
      serialized.userkey ??
      serialized.userKey;
    const graphObjectKey =
      rawKey != null && String(rawKey).trim() !== '' ? String(rawKey).trim() : undefined;
    const fullName = String(flat.Name__c || flat.name || serialized.label || '').trim();
    const [derivedFirstName = '', ...derivedLastNameParts] = fullName.split(/\s+/).filter(Boolean);
    const derivedLastName = derivedLastNameParts.join(' ');
    const status = serialized.status;
    const activeValue = flat.IsActive__c ?? flat.isActive ?? flat.active;
    const isExplicitlyInactive =
      activeValue === false ||
      activeValue === 0 ||
      activeValue === '0' ||
      String(activeValue ?? '').toLowerCase() === 'false';

    return {
      id: serialized.id?.toString() || String(serialized.id || ''),
      username: flat.username || flat.Username__c || flat.Username || '',
      email: flat.email || flat.Email__c || flat.Email || '',
      firstName: flat.firstname || flat.firstName || flat.FirstName__c || flat.FirstName || derivedFirstName,
      lastName: flat.lastname || flat.lastName || flat.LastName__c || flat.LastName || derivedLastName,
      graphObjectKey,
      phone: flat.phone || flat.Phone__c || undefined,
      title: flat.title || flat.Title__c || undefined,
      department: flat.department || flat.Department__c || undefined,
      location: flat.location || flat.Location__c || undefined,
      profilePicture: data.profilePicture || undefined,
      isActive: status == null
        ? !isExplicitlyInactive
        : status === 1 || status === '1' || status === true,
      lastLogin: data.lastLogin || undefined,
      createdDate: safeDateToString(serialized.creationdate || serialized.creationDate),
      createdBy: data.createdBy || 'System',
      lastModified: safeDateToString(serialized.modificationdate || serialized.modificationDate),
      locale: data.locale || undefined,
      timezone: data.timezone || undefined,
      emailSignature: data.emailSignature || undefined,
      keycloakUserId: flat.keycloakUserId || flat.KeycloakUserId__c || undefined,
      // Note: roleId and roleName are populated by getUserRoles() when fetching user
      roleId: undefined,
      roleName: undefined,
    };
  } catch (error) {
    console.error('[GobodoUserProvider] Error converting user:', error, gobodoUser);
    // Return a minimal user object to prevent breaking the app
    return {
      id: gobodoUser?.id?.toString() || String(gobodoUser?.id || 'unknown'),
      username: gobodoUser?.username || '',
      email: gobodoUser?.email || '',
      firstName: gobodoUser?.firstname || '',
      lastName: gobodoUser?.lastname || '',
      isActive: false,
      createdDate: new Date().toISOString(),
      createdBy: 'System',
      lastModified: new Date().toISOString(),
      roles: undefined,
      roleIds: undefined,
      roleNames: undefined,
      roleId: '',
      roleName: '',
    };
  }
}

/**
 * Convert our User to Gobodo UserDataPayload
 */
function convertToGobodoUserPayload(user: Partial<User>): GobodoApi.UserDataPayload {
  const payload = new GobodoApi.UserDataPayload();
  
  // Built-in fields
  if (user.username !== undefined) payload.username = user.username;
  if (user.email !== undefined) payload.email = user.email;
  if (user.firstName !== undefined) payload.firstname = user.firstName;
  if (user.lastName !== undefined) payload.lastname = user.lastName;
  if (user.isActive !== undefined) payload.status = user.isActive ? 1 : 0;
  
  // Custom fields stored in data property
  const data: Record<string, any> = {};
  if (user.phone !== undefined) data.phone = user.phone;
  if (user.title !== undefined) data.title = user.title;
  if (user.department !== undefined) data.department = user.department;
  if (user.location !== undefined) data.location = user.location;
  if (user.profilePicture !== undefined) data.profilePicture = user.profilePicture;
  if (user.lastLogin !== undefined) data.lastLogin = user.lastLogin;
  if (user.locale !== undefined) data.locale = user.locale;
  if (user.timezone !== undefined) data.timezone = user.timezone;
  if (user.emailSignature !== undefined) data.emailSignature = user.emailSignature;
  if (user.createdBy !== undefined) data.createdBy = user.createdBy;
  if (user.keycloakUserId !== undefined) data.keycloakUserId = user.keycloakUserId;
  
  // Only set data if it has properties
  if (Object.keys(data).length > 0) {
    payload.data = data;
  }
  
  return payload;
}

/** Same person across directory vs joined: graph key (both present and equal), then id, then email. */
function usersRepresentSamePerson(a: User, b: User): boolean {
  const ag = a.graphObjectKey?.trim();
  const bg = b.graphObjectKey?.trim();
  if (ag && bg && ag === bg) return true;
  const aid = String(a.id ?? '').trim();
  const bid = String(b.id ?? '').trim();
  if (aid && bid && aid === bid) return true;
  const ae = String(a.email ?? '').trim().toLowerCase();
  const be = String(b.email ?? '').trim().toLowerCase();
  if (ae && be && ae === be) return true;
  return false;
}

/** Directory order first; append joined-only rows. Directory wins when rows represent the same person. */
function mergeDirectoryWithJoinedUsers(directory: User[], joined: User[]): User[] {
  const out = [...directory];
  for (const j of joined) {
    if (!out.some((existing) => usersRepresentSamePerson(existing, j))) {
      out.push(j);
    }
  }
  return out;
}

function convertGobodoUserRowsToUsers(rows: unknown, operation: string): User[] {
  if (!rows || !Array.isArray(rows)) {
    return [];
  }
  const users: User[] = [];
  for (const gobodoUser of rows) {
    try {
      const user = convertFromGobodoUser(gobodoUser);
      if (user.id && user.username) {
        users.push(user);
      }
    } catch (error) {
      const gobodoError = handleGobodoError(error as any, { operation });
      if (gobodoError) {
        logGobodoError(gobodoError, 'warn');
      }
    }
  }
  return users;
}

/**
 * Get the Role object type key using the configured custom suffix
 */
function getRoleTypeKey(): string {
  return `Role${getCustomSuffix()}`;
}

/**
 * Get the User object type key (built-in Gobodo object)
 * Users are typically represented as "User" or "Account" in Gobodo
 */
function getUserObjectTypeKey(): string {
  // User is a built-in object type in Gobodo, typically keyed as "User"
  return 'User';
}

/**
 * Gobodo User Provider Implementation
 * 
 * Connects to Gobodo UsersApi for user CRUD operations.
 */
export class GobodoUserProvider {
  private async hydrateUserRoles(user: User): Promise<User> {
    if (!user.id) return user;
    try {
      const roles = await this.getUserRoles(user.id);
      return {
        ...user,
        roles,
        roleIds: roles.map(r => r.id),
        roleNames: roles.map(r => r.name),
        roleId: roles.length > 0 ? roles[0].id : undefined,
        roleName: roles.length > 0 ? roles[0].name : undefined,
      };
    } catch (error) {
      console.warn('[GobodoUserProvider] Failed to load roles for user:', error);
      return user;
    }
  }

  async getUsers(filters?: UserFilters): Promise<User[]> {
    try {
      const client = getGobodoClient();
      const usersApi = new GobodoApi.UsersApi(client);

      // Fetch joined users (app-scoped) if the SDK method exists, otherwise skip
      const joinedPromise = typeof (usersApi as any).getJoinedUsersForApplication === 'function'
        ? retryWithBackoff(
            () => (usersApi as any).getJoinedUsersForApplication(GobodoConfig.appId(), {
              startindex: 0,
              count: 1000,
            }),
            { maxRetries: 1, initialDelayMs: 2000 }
          ).catch((error: unknown) => {
            const gobodoError = handleGobodoError(error as any, { operation: 'getJoinedUsersForApplication' });
            if (gobodoError) logGobodoError(gobodoError, 'warn');
            return null;
          })
        : Promise.resolve(null);

      const [directoryRaw, joinedRaw] = await Promise.all([
        retryWithBackoff(
          () => usersApi.getUsers({ page: 0, count: 1000 }),
          { maxRetries: 1, initialDelayMs: 2000 }
        ),
        joinedPromise,
      ]);

      const directoryUsers = convertGobodoUserRowsToUsers(directoryRaw, 'getUsers');
      const joinedUsers = joinedRaw != null ? convertGobodoUserRowsToUsers(joinedRaw, 'getJoinedUsersForApplication') : [];
      const users = mergeDirectoryWithJoinedUsers(directoryUsers, joinedUsers);

      let filteredUsers = filters?.includeRoles
        ? await Promise.all(users.map((user) => this.hydrateUserRoles(user)))
        : users;
      
      if (filters?.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        filteredUsers = filteredUsers.filter(u =>
          u.firstName.toLowerCase().includes(query) ||
          u.lastName.toLowerCase().includes(query) ||
          u.email.toLowerCase().includes(query) ||
          u.username.toLowerCase().includes(query) ||
          u.department?.toLowerCase().includes(query) ||
          u.title?.toLowerCase().includes(query)
        );
      }

      if (filters?.status && filters.status !== 'all') {
        filteredUsers = filteredUsers.filter(u =>
          filters.status === 'active' ? u.isActive : !u.isActive
        );
      }

      if (filters?.department) {
        filteredUsers = filteredUsers.filter(u => u.department === filters.department);
      }

      if (filters?.roleId) {
        filteredUsers = filteredUsers.filter(u =>
          u.roleId === filters.roleId ||
          u.roleIds?.includes(filters.roleId as string)
        );
      }

      return filteredUsers.sort((a, b) => {
        const lastNameCompare = (a.lastName || '').localeCompare(b.lastName || '');
        if (lastNameCompare !== 0) return lastNameCompare;
        return (a.firstName || '').localeCompare(b.firstName || '');
      });
    } catch (error: any) {
      const gobodoError = handleGobodoError(error, { operation: 'getUsers' });
      if (gobodoError) {
        logGobodoError(gobodoError);
        throw gobodoError;
      }
      throw error;
    }
  }

  async getUsersForRoleIds(roleIds: string[], roleNamesById: Record<string, string> = {}): Promise<User[]> {
    try {
      const numericRoleIds = Array.from(
        new Set(roleIds.map((id) => parseInt(id, 10)).filter((id) => !Number.isNaN(id)))
      );
      if (numericRoleIds.length === 0) return [];

      const client = getGobodoClient();
      const traversalApi = new GobodoApi.GraphTraversalApi(client);
      const relationshipTypesApi = new GobodoApi.GraphRelationshipTypesApi(client);
      const relationshipKey = `role_user${getCustomSuffix()}`;

      try {
        await retryWithBackoff(
          () => relationshipTypesApi.getRelationshipTypeByKey(relationshipKey),
          { maxRetries: 2, initialDelayMs: 750 }
        );
      } catch (error: any) {
        const status = error?.status ?? error?.response?.status ?? error?.response?.statusCode;
        if (status === 404) {
          console.warn(`[GobodoUserProvider] Role-user relationship ${relationshipKey} not found; returning no role assignments.`);
          return [];
        }
        throw error;
      }

      const usersById = new Map<string, User>();
      for (const roleId of numericRoleIds) {
        const traversalInput = new GobodoApi.GraphTraversalInput();
        traversalInput.startObjectIds = [roleId];
        traversalInput.relationshipTypes = [relationshipKey];
        traversalInput.direction = GobodoApi.GraphTraversalInput.DirectionEnum.OUTGOING;
        traversalInput.maxPathLength = 1;
        traversalInput.endObjectTypes = [getUserObjectTypeKey(), 'USER'];

        const traversalResult = await retryWithBackoff(
          () => traversalApi.traverseFromFindObjects(roleId, traversalInput),
          { maxRetries: 2, initialDelayMs: 750 }
        );
        if (!Array.isArray(traversalResult)) continue;

        for (const gobodoUser of traversalResult) {
          const user = convertFromGobodoUser(gobodoUser);
          const key = user.graphObjectKey || user.id || user.email;
          if (!key) continue;

          const roleIdText = String(roleId);
          const roleName = String(roleNamesById[roleIdText] ?? '').trim();
          const existing = usersById.get(key);
          const roleIdsForUser = Array.from(new Set([...(existing?.roleIds ?? []), roleIdText]));
          const roleNamesForUser = Array.from(new Set([...(existing?.roleNames ?? []), ...(roleName ? [roleName] : [])]));
          usersById.set(key, {
            ...(existing ?? user),
            roleIds: roleIdsForUser,
            roleNames: roleNamesForUser,
            roleId: existing?.roleId ?? user.roleId ?? roleIdText,
            roleName: existing?.roleName ?? user.roleName ?? roleNamesForUser[0],
          });
        }
      }

      return Array.from(usersById.values()).sort((a, b) => {
        const lastNameCompare = (a.lastName || '').localeCompare(b.lastName || '');
        if (lastNameCompare !== 0) return lastNameCompare;
        return (a.firstName || '').localeCompare(b.firstName || '');
      });
    } catch (error: any) {
      const status = error?.status ?? error?.response?.status ?? error?.response?.statusCode;
      console.warn(`[GobodoUserProvider] Failed to get users for roles (status ${status ?? '?'}).`);
      throw error;
    }
  }

  async getUserById(id: string): Promise<User | null> {
    try {
      const client = getGobodoClient();
      const usersApi = new GobodoApi.UsersApi(client);

      const numericId = parseInt(id, 10);
      if (isNaN(numericId)) {
        return null;
      }

      const response = await retryWithBackoff(
        () => usersApi.getUserById(numericId),
        { maxRetries: 3, initialDelayMs: 1500 }
      );
      
      if (!response) {
        return null;
      }

      const user = convertFromGobodoUser(response);
      
      if (user.id) {
        try {
          const roles = await this.getUserRoles(user.id);
          user.roles = roles;
          user.roleIds = roles.map(r => r.id);
          user.roleNames = roles.map(r => r.name);
          user.roleId = roles.length > 0 ? roles[0].id : undefined;
          user.roleName = roles.length > 0 ? roles[0].name : undefined;
        } catch (error) {
          console.warn('[GobodoUserProvider] Failed to load roles for user:', error);
        }
      }
      
      return user;
    } catch (error: any) {
      if (error.response?.status === 404 || error.status === 404) {
        return null;
      }
      // Don't swallow 500s — throw so callers know it's a transient failure,
      // not "user doesn't exist"
      const gobodoError = handleGobodoError(error, { operation: 'getUserById', userId: id });
      if (gobodoError) {
        logGobodoError(gobodoError, 'warn');
        throw gobodoError;
      }
      throw error;
    }
  }

  async getUserByUsername(username: string): Promise<User | null> {
    try {
      const client = getGobodoClient();
      const usersApi = new GobodoApi.UsersApi(client);

      const response = await retryWithBackoff(
        () => usersApi.getUserByUserName(username),
        { maxRetries: 2, initialDelayMs: 1000 }
      );
      
      if (!response) {
        return null;
      }

      return convertFromGobodoUser(response);
    } catch (error: any) {
      if (error.status === 404) {
        return null;
      }
      console.error('[GobodoUserProvider] Failed to fetch user by username from Gobodo:', error);
      throw error;
    }
  }

  async getSelf(): Promise<User | null> {
    try {
      const client = getGobodoClient();
      const usersApi = new GobodoApi.UsersApi(client);

      const response = await retryWithBackoff(
        () => usersApi.getSelf(),
        { maxRetries: 3, initialDelayMs: 1500 }
      );
      
      if (!response) {
        return null;
      }

      const user = convertFromGobodoUser(response);
      
      // Populate roles for current user
      if (user.id) {
        try {
          const roles = await this.getUserRoles(user.id);
          user.roles = roles;
          user.roleIds = roles.map(r => r.id);
          user.roleNames = roles.map(r => r.name);
          user.roleId = roles.length > 0 ? roles[0].id : undefined;
          user.roleName = roles.length > 0 ? roles[0].name : undefined;
        } catch (error) {
          console.warn('[GobodoUserProvider] Failed to load roles for current user:', error);
        }
      }
      
      return user;
    } catch (error: any) {
      console.error('[GobodoUserProvider] Failed to fetch current user from Gobodo:', error);
      throw error;
    }
  }

  /**
   * Get user by email address
   * Fetches all users and filters by email client-side
   */
  async getUserByEmail(email: string): Promise<User | null> {
    try {
      const users = await this.getUsers();
      const user = users.find(u => u.email.toLowerCase() === email.toLowerCase()) || null;
      
      // Populate roles if user found
      if (user && user.id) {
        try {
          const roles = await this.getUserRoles(user.id);
          user.roles = roles;
          user.roleIds = roles.map(r => r.id);
          user.roleNames = roles.map(r => r.name);
          user.roleId = roles.length > 0 ? roles[0].id : undefined;
          user.roleName = roles.length > 0 ? roles[0].name : undefined;
        } catch (error) {
          console.warn('[GobodoUserProvider] Failed to load roles for user:', error);
        }
      }
      
      return user;
    } catch (error) {
      console.error('[GobodoUserProvider] Failed to fetch user by email:', error);
      return null;
    }
  }

  /**
   * Get user by Keycloak user ID
   * Fetches all users and filters by keycloakUserId stored in data property
   */
  async getUserByKeycloakId(keycloakId: string): Promise<User | null> {
    try {
      const users = await this.getUsers();
      // KeycloakUserId is stored in user.data.keycloakUserId (extracted by convertFromGobodoUser)
      const user = users.find(u => u.keycloakUserId === keycloakId) || null;
      
      // Populate roles if user found
      if (user && user.id) {
        try {
          const roles = await this.getUserRoles(user.id);
          user.roles = roles;
          user.roleIds = roles.map(r => r.id);
          user.roleNames = roles.map(r => r.name);
          user.roleId = roles.length > 0 ? roles[0].id : undefined;
          user.roleName = roles.length > 0 ? roles[0].name : undefined;
        } catch (error) {
          console.warn('[GobodoUserProvider] Failed to load roles for user:', error);
        }
      }
      
      return user;
    } catch (error) {
      console.error('[GobodoUserProvider] Failed to fetch user by Keycloak ID:', error);
      return null;
    }
  }

  /**
   * Assign a single role to a user (wrapper for assignRolesToUser)
   */
  async assignRole(userId: string, roleId: string): Promise<void> {
    await this.assignRolesToUser(userId, [roleId]);
  }

  /**
   * Delete a user (soft delete by deactivating)
   */
  async deleteUser(id: string): Promise<void> {
    try {
      await this.updateUser(id, { isActive: false });
    } catch (error) {
      console.error('[GobodoUserProvider] Failed to delete user:', error);
      throw error;
    }
  }

  async createUser(request: CreateUserPayload): Promise<User> {
    try {
      // Validate required fields
      if (!request.username || !request.username.trim()) {
        throw new Error('Username is required');
      }
      if (!request.email || !request.email.trim()) {
        throw new Error('Email is required');
      }
      if (!request.firstName || !request.firstName.trim()) {
        throw new Error('First name is required');
      }
      if (!request.lastName || !request.lastName.trim()) {
        throw new Error('Last name is required');
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(request.email.trim())) {
        throw new Error('Invalid email format');
      }

      const client = getGobodoClient();
      const usersApi = new GobodoApi.UsersApi(client);

      const userData: Partial<User> = {
        username: request.username.trim(),
        email: request.email.trim().toLowerCase(),
        firstName: request.firstName.trim(),
        lastName: request.lastName.trim(),
        phone: request.phone?.trim(),
        title: request.title?.trim(),
        department: request.department?.trim(),
        locale: request.locale,
        timezone: request.timezone,
        isActive: request.isActive ?? true,
        createdBy: 'Current User',
        keycloakUserId: request.keycloakUserId,
      };

      const payload = convertToGobodoUserPayload(userData);

      // Set initial password (required by backend for new accounts)
      const envDefault =
        typeof process !== 'undefined' && process.env?.REACT_APP_DEFAULT_USER_PASSWORD
          ? process.env.REACT_APP_DEFAULT_USER_PASSWORD
          : '';
      const initialPassword = request.password ?? envDefault;
      payload.password = initialPassword;

      const response = await retryWithBackoff(
        () => usersApi.createUser(payload),
        { maxRetries: 2 }
      );
      
      const user = convertFromGobodoUser(response);

      if (request.roleIds && request.roleIds.length > 0) {
        try {
          await this.assignRolesToUser(user.id, request.roleIds);
          const roles = await this.getUserRoles(user.id);
          user.roles = roles;
          user.roleIds = roles.map(r => r.id);
          user.roleNames = roles.map(r => r.name);
          user.roleId = roles.length > 0 ? roles[0].id : undefined;
          user.roleName = roles.length > 0 ? roles[0].name : undefined;
        } catch (error) {
          const gobodoError = handleGobodoError(error as any, { operation: 'createUser', userId: user.id });
          if (gobodoError) {
            logGobodoError(gobodoError, 'warn');
          }
        }
      }
      
      return user;
    } catch (error: any) {
      const gobodoError = handleGobodoError(error, { operation: 'createUser', email: request.email });
      if (gobodoError) {
        logGobodoError(gobodoError);
        throw gobodoError;
      }
      throw error;
    }
  }

  async updateUser(id: string, updates: Partial<User>): Promise<User> {
    try {
      const client = getGobodoClient();
      const usersApi = new GobodoApi.UsersApi(client);

      const numericId = parseInt(id, 10);
      if (isNaN(numericId)) {
        throw new Error(`Invalid user ID format for update: ${id}`);
      }

      // Get existing user to merge updates
      const existingUser = await this.getUserById(id);
      if (!existingUser) {
        throw new Error(`User with ID ${id} not found`);
      }

      // Merge updates into existing user data
      // Only include fields that are actually being updated (not undefined)
      const updatedUserData: Partial<User> = {
        ...existingUser,
      };

      // Apply updates only for defined fields
      Object.keys(updates).forEach(key => {
        if (updates[key as keyof User] !== undefined) {
          (updatedUserData as any)[key] = updates[key as keyof User];
        }
      });

      // Always update lastModified
      updatedUserData.lastModified = new Date().toISOString();

      // Convert to Gobodo payload
      const payload = convertToGobodoUserPayload(updatedUserData);

      // Update user
      const response = await retryWithBackoff(
        () => usersApi.updateUser(numericId, payload),
        { maxRetries: 3 }
      );
      
      if (!response) {
        throw new Error('Update response was empty');
      }
      
      return convertFromGobodoUser(response);
    } catch (error: any) {
      const gobodoError = handleGobodoError(error, { operation: 'updateUser', userId: id });
      if (gobodoError) {
        logGobodoError(gobodoError);
        throw gobodoError;
      }
      throw error;
    }
  }

  /**
   * Update last login timestamp for the current user
   * Uses getSelf() to get the current user and updates their lastLogin field
   * @returns Promise that resolves when lastLogin is updated
   */
  async updateLastLogin(): Promise<void> {
    try {
      const user = await this.getSelf();
      if (!user || !user.id) {
        console.warn('[GobodoUserProvider] Cannot update lastLogin: user not found via getSelf()');
        return;
      }

      // Update lastLogin to current timestamp
      const lastLogin = new Date().toISOString();
      await this.updateUser(user.id, { lastLogin });
      
      console.log(`[GobodoUserProvider] Successfully updated lastLogin for user ${user.id}`);
    } catch (error: any) {
      // Don't throw error - log warning but don't fail the sign-in process
      console.warn('[GobodoUserProvider] Failed to update lastLogin:', error);
    }
  }

  /**
   * Add user to graph
   * Adds the user to the specified graph so they can be queried as a graph object
   * Uses getSelf() if no userId provided, otherwise uses the provided userId
   * The object ID corresponds to the user ID returned from UsersApi
   * @param userId - Optional user ID (numeric or string). If not provided, uses getSelf() to get current user
   * @returns Promise that resolves when user is added to graph
   */
  async addUserToGraph(userId?: string | number): Promise<void> {
    try {
      const client = getGobodoClient();
      const graphsApi = new GobodoApi.GraphsApi(client);

      let numericId: number;

      if (userId !== undefined) {
        // Use provided userId
        numericId = typeof userId === 'string' ? parseInt(userId, 10) : userId;
        if (isNaN(numericId)) {
          throw new Error(`Invalid user ID format: ${userId}`);
        }
      } else {
        // Use getSelf() to get the logged-in user
        const user = await this.getSelf();
        if (!user || !user.id) {
          console.warn('[GobodoUserProvider] Cannot add user to graph: user not found via getSelf()');
          return;
        }
        numericId = parseInt(user.id, 10);
        if (isNaN(numericId)) {
          throw new Error(`Invalid user ID format from getSelf(): ${user.id}`);
        }
      }

      // The object ID corresponds to the user ID from UsersApi
      // Use GraphsApi.addObjectsToGraphById() to add the user to the graph
      const graphId = GobodoConfig.graph();
      const objectIds = [numericId]; // Array of object IDs (user IDs)
      
      await retryWithBackoff(
        () => graphsApi.addObjectsToGraphById(graphId, objectIds),
        { maxRetries: 3 }
      );
      
      console.log(`[GobodoUserProvider] Successfully added user ${numericId} to graph ${graphId}`);
    } catch (error: any) {
      // Don't throw error - user might already be in graph or there might be other issues
      // Log warning but don't fail the sign-in process
      console.warn(`[GobodoUserProvider] Failed to add user to graph:`, error);
      
      // If error is 404, user might not exist as a graph object yet
      // If error is 409 or similar, user might already be in graph
      // In either case, we don't want to fail the sign-in
    }
  }

  /**
   * Ensure the role_user__c relationship type exists
   * Creates it with MANY_TO_MANY cardinality if it doesn't exist
   * @returns Promise that resolves with the relationship type ID
   */
  async ensureRoleUserRelationshipType(): Promise<number> {
    try {
      const client = getGobodoClient();
      const relationshipTypesApi = new GobodoApi.GraphRelationshipTypesApi(client);
      const objectTypesApi = new GobodoApi.GraphObjectTypesApi(client);
      const schemasApi = new GobodoApi.GraphSchemasApi(client);

      const relationshipKey = `role_user${getCustomSuffix()}`;
      const systemSchemaId = getSchemaId('system');
      
      // Check if relationship type already exists
      let relationshipTypeId: number;
      try {
        const existing = await retryWithBackoff(
          () => relationshipTypesApi.getRelationshipTypeByKey(relationshipKey),
          { maxRetries: 3, initialDelayMs: 1000 }
        );
        relationshipTypeId = existing.id;
        
        // Schema verification is non-critical; swallow errors silently
        try {
          const schemaRelationshipTypes = await retryWithBackoff(
            () => schemasApi.getSchemaRelationshipTypes(systemSchemaId),
            { maxRetries: 3 }
          );
          const isInSchema = schemaRelationshipTypes?.some((rt: any) => rt.id === relationshipTypeId);
          if (!isInSchema) {
            await retryWithBackoff(
              () => schemasApi.addSchemaRelationshipTypes(systemSchemaId, [relationshipTypeId]),
              { maxRetries: 3 }
            );
          }
        } catch {
          // Non-critical: relationship type exists, schema verification can fail on transient 500
        }
        
        return relationshipTypeId;
      } catch (error: any) {
        // Relationship type doesn't exist, create it
        if (error.status !== 404) {
          throw error; // Re-throw if it's not a 404
        }
      }

      // Get Role object type
      const roleTypeKey = getRoleTypeKey();
      const roleObjectType = await retryWithBackoff(
        () => objectTypesApi.getObjectTypeByKey(roleTypeKey, { maxFields: 0 }),
        { maxRetries: 3 }
      );
      
      // Try to find User object type - built-in UsersApi users may not have a graph object type
      const userTypeKey = getUserObjectTypeKey();
      let userObjectType: any = null;
      
      try {
        userObjectType = await retryWithBackoff(
          () => objectTypesApi.getObjectTypeByKey(userTypeKey, { maxFields: 0 }),
          { maxRetries: 3 }
        );
      } catch (error: any) {
        // Try "Account" as fallback if "User" doesn't work
        if (error.status === 404) {
          try {
            userObjectType = await retryWithBackoff(
              () => objectTypesApi.getObjectTypeByKey('Account', { maxFields: 0 }),
              { maxRetries: 3 }
            );
          } catch (e: any) {
            // Built-in UsersApi users don't have a corresponding graph object type
            // Try creating relationship type without endObjectTypes - Gobodo may allow this
            console.warn(
              `[GobodoUserProvider] User object type not found (tried "${userTypeKey}" and "Account"). ` +
              `Attempting to create relationship type without endObjectTypes - built-in users may not require it.`
            );
          }
        } else {
          throw error;
        }
      }

      // Create relationship type input with MANY_TO_MANY cardinality
      const relationshipInput = new GobodoApi.GraphRelationshipTypeInput([], [], {});
      relationshipInput.key = relationshipKey;
      relationshipInput.name = 'Role User';
      relationshipInput.description = 'Many-to-many relationship between Roles and Users';
      relationshipInput.descriptiveName = 'Role User';
      relationshipInput.reverseDescriptiveName = 'User Role';
      relationshipInput.createName = 'Role User';
      relationshipInput.reverseName = 'User Role';
      relationshipInput.pluralName = 'Users';
      relationshipInput.reversePluralName = 'Roles';
      relationshipInput.cardinality = GobodoApi.GraphRelationshipTypeInput.CardinalityEnum.MANY_TO_MANY;
      
      // Use system schema since roles are system objects
      relationshipInput.schemaid = systemSchemaId;

      // Set start object type (Role)
      const startObjectTypeInput = new GobodoApi.GraphObjectTypeInput([], [], {});
      startObjectTypeInput.id = roleObjectType.id;
      startObjectTypeInput.key = roleObjectType.key;
      startObjectTypeInput.name = roleObjectType.name;
      relationshipInput.startObjectTypes = [startObjectTypeInput];

      // Set end object type only if we found a User object type
      // Built-in UsersApi users may not require this
      if (userObjectType) {
        const endObjectTypeInput = new GobodoApi.GraphObjectTypeInput([], [], {});
        endObjectTypeInput.id = userObjectType.id;
        endObjectTypeInput.key = userObjectType.key;
        endObjectTypeInput.name = userObjectType.name;
        relationshipInput.endObjectTypes = [endObjectTypeInput];
      } else {
        // Try without endObjectTypes - Gobodo may allow relationships to built-in users without explicit object type
        console.warn(
          `[GobodoUserProvider] Creating relationship type ${relationshipKey} without endObjectTypes. ` +
          `This may work if Gobodo allows relationships to built-in UsersApi users without a graph object type.`
        );
        relationshipInput.endObjectTypes = [];
      }

      // Create relationship type (with system schema ID set)
      const response = await retryWithBackoff(
        () => relationshipTypesApi.createRelationshipType(relationshipInput),
        { maxRetries: 3 }
      );

      // Explicitly add relationship type to system schema
      // This ensures it's properly registered even if creation didn't automatically add it
      await retryWithBackoff(
        () => schemasApi.addSchemaRelationshipTypes(systemSchemaId, [response.id]),
        { maxRetries: 3 }
      );

      console.log(`[GobodoUserProvider] Created relationship type ${relationshipKey} with ID ${response.id} and added to system schema (ID: ${systemSchemaId})`);
      return response.id;
    } catch (error: any) {
      const status = error?.status ?? error?.response?.statusCode ?? error?.statusCode;
      const msg = typeof error?.message === 'string' && !error.message.startsWith('<!') ? error.message : 'Backend error';
      console.warn(`[GobodoUserProvider] Failed to ensure role_user relationship type (status ${status ?? '?'}): ${msg}`);
      throw error;
    }
  }

  /**
   * Assign roles to a user by creating relationship instances
   * @param userId - User ID (string or numeric)
   * @param roleIds - Array of role IDs to assign
   */
  async assignRolesToUser(userId: string | number, roleIds: string[]): Promise<void> {
    if (!roleIds || roleIds.length === 0) {
      return; // Nothing to assign
    }

    try {
      const client = getGobodoClient();
      const relationshipsApi = new GobodoApi.GraphRelationshipsApi(client);
      const relationshipTypesApi = new GobodoApi.GraphRelationshipTypesApi(client);
      const objectsApi = new GobodoApi.GraphObjectsApi(client);

      // Ensure relationship type exists
      const relationshipKey = `role_user${getCustomSuffix()}`;
      await retryWithBackoff(() => this.ensureRoleUserRelationshipType(), { maxRetries: 2 });

      // Get relationship type to verify it exists and get the full object
      const relationshipType = await retryWithBackoff(
        () => relationshipTypesApi.getRelationshipTypeByKey(relationshipKey),
        { maxRetries: 2 }
      );

      if (!relationshipType || !relationshipType.id) {
        const error = new Error(`Relationship type ${relationshipKey} not found after creation`);
        const gobodoError = handleGobodoError(error, { operation: 'assignRolesToUser', objectType: relationshipKey });
        if (gobodoError) {
          logGobodoError(gobodoError);
          throw gobodoError;
        }
        throw error;
      }

      // Convert userId to numeric - handle both string and numeric IDs
      let numericUserId: number;
      if (typeof userId === 'string') {
        // Try parsing as number first
        numericUserId = parseInt(userId, 10);
        if (isNaN(numericUserId)) {
          // If not numeric, try to get user by email or other identifier
          const user = await this.getUserByEmail(userId).catch(() => this.getUserById(userId));
          if (!user || !user.id) {
            throw new Error(`Invalid user ID format: ${userId}`);
          }
          numericUserId = parseInt(user.id, 10);
          if (isNaN(numericUserId)) {
            throw new Error(`User ID ${user.id} is not numeric`);
          }
        }
      } else {
        numericUserId = userId;
      }

      // Create relationship instances for each role
      const results: Array<{ roleId: string; success: boolean; error?: string }> = [];
      
      for (const roleId of roleIds) {
        const numericRoleId = parseInt(roleId, 10);
        if (isNaN(numericRoleId)) {
          console.warn(`[GobodoUserProvider] Skipping invalid role ID: ${roleId}`);
          results.push({ roleId, success: false, error: 'Invalid role ID format' });
          continue;
        }

        try {
          // Verify role exists with retry
          await retryWithBackoff(
            () => objectsApi.getObjectById(numericRoleId),
            { maxRetries: 2 }
          );
        } catch (error: any) {
          const gobodoError = handleGobodoError(error, {
            operation: 'assignRolesToUser',
            roleId: numericRoleId,
            userId: numericUserId
          });
          if (gobodoError) {
            logGobodoError(gobodoError, 'warn');
            results.push({ roleId, success: false, error: gobodoError.message });
          } else {
            results.push({ roleId, success: false, error: 'Unknown error' });
          }
          continue;
        }

        // Create relationship payload
        // Direction: Role (start) → User (end)
        // createRelationship(id, payload) where id is the start object ID (Role)
        const relationshipPayload = new GobodoApi.GraphRelationshipPayload();
        relationshipPayload.relationshiptypeid = relationshipType.id;
        relationshipPayload.to = numericUserId; // User (end object)

        try {
          await retryWithBackoff(
            () => relationshipsApi.createRelationship(numericRoleId, relationshipPayload),
            { maxRetries: 2 }
          );
          console.log(`[GobodoUserProvider] Created relationship: Role ${numericRoleId} → User ${numericUserId}`);
          results.push({ roleId, success: true });
        } catch (error: any) {
          // If relationship already exists (409), that's okay
          if (error.response?.status === 409 || error.status === 409) {
            console.log(`[GobodoUserProvider] Relationship already exists: Role ${numericRoleId} → User ${numericUserId}`);
            results.push({ roleId, success: true });
          } else {
            const gobodoError = handleGobodoError(error, {
              operation: 'assignRolesToUser',
              roleId: numericRoleId,
              userId: numericUserId
            });
            if (gobodoError) {
              logGobodoError(gobodoError);
              results.push({ roleId, success: false, error: gobodoError.message });
            } else {
              results.push({ roleId, success: false, error: 'Unknown error' });
            }
          }
        }
      }

      // If all failed, throw error
      const failed = results.filter(r => !r.success);
      if (failed.length === results.length) {
        const error = new Error(`Failed to assign any roles. Errors: ${failed.map(f => f.error).join(', ')}`);
        const gobodoError = handleGobodoError(error, {
          operation: 'assignRolesToUser',
          userId: numericUserId,
          roleIds
        });
        throw gobodoError;
      }

      // Log partial failures
      if (failed.length > 0) {
        console.warn(`[GobodoUserProvider] Some role assignments failed:`, failed);
      }
    } catch (error: any) {
      const gobodoError = handleGobodoError(error, {
        operation: 'assignRolesToUser',
        userId,
        roleIds
      });
      logGobodoError(gobodoError);
      throw gobodoError;
    }
  }

  /**
   * Remove roles from a user by deleting relationship instances
   * @param userId - User ID (string or numeric)
   * @param roleIds - Array of role IDs to remove
   */
  async removeRolesFromUser(userId: string | number, roleIds: string[]): Promise<void> {
    if (!roleIds || roleIds.length === 0) {
      return; // Nothing to remove
    }

    try {
      const client = getGobodoClient();
      const relationshipsApi = new GobodoApi.GraphRelationshipsApi(client);
      const relationshipTypesApi = new GobodoApi.GraphRelationshipTypesApi(client);

      const relationshipKey = `role_user${getCustomSuffix()}`;
      
      // Get relationship type
      let relationshipType: any;
      try {
        relationshipType = await retryWithBackoff(
          () => relationshipTypesApi.getRelationshipTypeByKey(relationshipKey),
          { maxRetries: 3 }
        );
      } catch (error: any) {
        if (error.status === 404) {
          console.log(`[GobodoUserProvider] Relationship type ${relationshipKey} doesn't exist, nothing to remove`);
          return;
        }
        throw error;
      }

      // Convert userId to numeric
      const numericUserId = typeof userId === 'string' ? parseInt(userId, 10) : userId;
      if (isNaN(numericUserId)) {
        throw new Error(`Invalid user ID format: ${userId}`);
      }

      // Delete relationship instances for each role
      for (const roleId of roleIds) {
        const numericRoleId = parseInt(roleId, 10);
        if (isNaN(numericRoleId)) {
          console.warn(`[GobodoUserProvider] Skipping invalid role ID: ${roleId}`);
          continue;
        }

        try {
          // Find and delete the relationship
          // Since relationship is Role → User, from User's perspective these are INCOMING relationships
          // Use getRelationshipsByTypeIn on the User object to find relationships of this type
          const objectsApi = new GobodoApi.GraphObjectsApi(client);
          const incomingRelationships = await retryWithBackoff(
            () => objectsApi.getRelationshipsByTypeIn(
              numericUserId,
              relationshipType.id
            ),
            { maxRetries: 3 }
          );

          if (incomingRelationships && incomingRelationships.length > 0) {
            // Find the relationship where startObjectId matches the role
            const relationshipToDelete = incomingRelationships.find(
              (rel: any) => rel.startObjectId === numericRoleId
            );

            if (relationshipToDelete) {
              await retryWithBackoff(
                () => relationshipsApi.deleteRelationship(relationshipToDelete.id),
                { maxRetries: 3 }
              );
              console.log(`[GobodoUserProvider] Deleted relationship: Role ${numericRoleId} → User ${numericUserId}`);
            }
          }
        } catch (error: any) {
          // If relationship doesn't exist (404), that's okay
          if (error.status === 404) {
            console.log(`[GobodoUserProvider] Relationship not found: Role ${numericRoleId} → User ${numericUserId}`);
          } else {
            console.warn(`[GobodoUserProvider] Failed to delete relationship for role ${roleId}:`, error);
            // Continue with other roles even if one fails
          }
        }
      }
    } catch (error: any) {
      console.error('[GobodoUserProvider] Failed to remove roles from user:', error);
      throw error;
    }
  }

  /**
   * Get all roles assigned to a user using Graph Traversal API
   * @param userId - User ID (string or numeric)
   * @returns Array of Role objects
   */
  async getUserRoles(userId: string | number): Promise<Role[]> {
    try {
      const client = getGobodoClient();
      const traversalApi = new GobodoApi.GraphTraversalApi(client);
      const relationshipTypesApi = new GobodoApi.GraphRelationshipTypesApi(client);

      const numericUserId = typeof userId === 'string' ? parseInt(userId, 10) : userId;
      if (isNaN(numericUserId)) {
        throw new Error(`Invalid user ID format: ${userId}`);
      }

      const relationshipKey = `role_user${getCustomSuffix()}`;
      
      // Look up relationship type directly (with retry). Skip ensureRoleUserRelationshipType
      // on the read path — it's expensive and the type is created during role assignment.
      try {
        await retryWithBackoff(
          () => relationshipTypesApi.getRelationshipTypeByKey(relationshipKey),
          { maxRetries: 3, initialDelayMs: 1000 }
        );
      } catch (error: any) {
        if (error.status === 404) {
          return []; // Relationship type doesn't exist yet — user has no roles
        }
        throw error;
      }

      const traversalInput = new GobodoApi.GraphTraversalInput();
      traversalInput.startObjectIds = [numericUserId];
      traversalInput.relationshipTypes = [relationshipKey];
      traversalInput.direction = GobodoApi.GraphTraversalInput.DirectionEnum.INCOMING;
      traversalInput.maxPathLength = 1;
      traversalInput.endObjectTypes = [getRoleTypeKey()];

      const traversalResult = await retryWithBackoff(
        () => traversalApi.traverseFromFindObjects(numericUserId, traversalInput),
        { maxRetries: 2, initialDelayMs: 1000 }
      );

      if (!traversalResult || !Array.isArray(traversalResult) || traversalResult.length === 0) {
        return [];
      }

      const roles: Role[] = [];
      for (const gobodoRole of traversalResult) {
        try {
          roles.push(convertFromGobodoRole(gobodoRole));
        } catch (error) {
          console.warn('[GobodoUserProvider] Failed to convert role:', error);
        }
      }

      return roles;
    } catch (error: any) {
      const status = error?.status ?? error?.response?.statusCode;
      console.warn(`[GobodoUserProvider] Failed to get user roles (status ${status ?? '?'}). Using Keycloak roles.`);
      return [];
    }
  }
}

// Export singleton instance
export const gobodoUserProvider = new GobodoUserProvider();

// Export factory for custom configuration
export function createGobodoUserProvider(): GobodoUserProvider {
  return new GobodoUserProvider();
}
